import * as THREE from 'three';
import type { ColliderDef, CollisionOptions, ICollisionSystem } from './colliderTypes';
import {
  resolveBoxCollision,
  resolveCylinderCollision,
  resolveSphereCollision,
  computeSlideDirection,
  rayIntersectsBox,
  rayIntersectsCylinder,
  rayIntersectsSphere,
  type CollisionResult,
} from './collisionMath';

interface AnalyticalCollisionConfig {
  cameraRadius: number;
  enabled: boolean;
  epsilon: number;
}

const MAX_RECURSION_DEPTH = 3;

export class AnalyticalCollisionSystem implements ICollisionSystem {
  private colliders: ColliderDef[];
  private config: AnalyticalCollisionConfig;

  // Scratch objects for zero-allocation hot path
  private readonly _scratchResult: CollisionResult = {
    position: new THREE.Vector3(),
    normal: new THREE.Vector3(),
    collided: false,
  };
  private readonly _scratchSafePos = new THREE.Vector3();
  private readonly _scratchSlideDir = new THREE.Vector3();
  private readonly _scratchSlideTarget = new THREE.Vector3();
  private readonly _scratchRayDir = new THREE.Vector3();
  private readonly _scratchCylCenter = new THREE.Vector3();
  private readonly _scratchSphereCenter = new THREE.Vector3();

  constructor(colliders: ColliderDef[], config: Partial<AnalyticalCollisionConfig> = {}) {
    this.colliders = colliders;
    this.config = {
      cameraRadius: config.cameraRadius ?? 0.05,
      enabled: config.enabled ?? true,
      epsilon: config.epsilon ?? 0.0001,
    };
  }

  getSafePosition(
    currentPosition: THREE.Vector3,
    desiredPosition: THREE.Vector3,
    options?: CollisionOptions,
  ): THREE.Vector3 {
    const slide = options?.slide ?? true;
    return this.resolve(currentPosition, desiredPosition, 0, slide);
  }

  private resolve(
    currentPosition: THREE.Vector3,
    desiredPosition: THREE.Vector3,
    recursionDepth: number,
    slide: boolean,
  ): THREE.Vector3 {
    if (!this.config.enabled || this.colliders.length === 0) {
      return desiredPosition;
    }
    if (recursionDepth > MAX_RECURSION_DEPTH) {
      return currentPosition;
    }

    const epsilon = this.config.epsilon;
    const cameraRadius = this.config.cameraRadius;

    // Start with desired position, resolve each collider sequentially
    const resolved = this._scratchSafePos.copy(desiredPosition);
    let lastNormal: THREE.Vector3 | null = null;
    let anyCollided = false;

    for (const collider of this.colliders) {
      if (!collider.enabled) {
        continue;
      }

      const result = this._scratchResult;

      if (collider.type === 'box') {
        resolveBoxCollision(resolved, collider, cameraRadius, result);
      } else if (collider.type === 'cylinder') {
        resolveCylinderCollision(resolved, collider, cameraRadius, result);
      } else {
        resolveSphereCollision(resolved, collider, cameraRadius, result);
      }

      if (result.collided) {
        resolved.copy(result.position);
        lastNormal = result.normal;
        anyCollided = true;
      }
    }

    if (!anyCollided) {
      return desiredPosition;
    }

    // Surface sliding: project remaining movement onto the collision plane
    if (slide && lastNormal) {
      const remainingX = desiredPosition.x - resolved.x;
      const remainingY = desiredPosition.y - resolved.y;
      const remainingZ = desiredPosition.z - resolved.z;
      const remainingLenSq = remainingX * remainingX + remainingY * remainingY + remainingZ * remainingZ;

      if (remainingLenSq > epsilon * epsilon) {
        this._scratchSlideDir.set(remainingX, remainingY, remainingZ);
        computeSlideDirection(this._scratchSlideDir, lastNormal, this._scratchSlideDir);

        if (this._scratchSlideDir.lengthSq() > epsilon * epsilon) {
          const slideTarget = this._scratchSlideTarget.copy(resolved).add(this._scratchSlideDir);
          // Clone resolved for recursion since scratch will be reused
          const savedResolved = resolved.clone();
          const slideResult = this.resolve(savedResolved, slideTarget.clone(), recursionDepth + 1, slide);
          if (!slideResult.equals(savedResolved)) {
            return slideResult;
          }
        }
      }
    }

    // Return a clone since resolved uses scratch memory
    return resolved.clone();
  }

  isLineOfSightObstructed(
    start: THREE.Vector3,
    target: THREE.Vector3,
    padding: number = Math.max(this.config.cameraRadius, this.config.epsilon * 10),
  ): boolean {
    if (!this.config.enabled || this.colliders.length === 0) {
      return false;
    }

    const dir = this._scratchRayDir.subVectors(target, start);
    const totalDist = dir.length();

    if (totalDist <= padding) {
      return false;
    }

    dir.normalize();
    const checkDist = totalDist - padding;

    for (const collider of this.colliders) {
      if (!collider.enabled || collider.blocksLineOfSight === false) {
        continue;
      }

      if (collider.type === 'box') {
        const t = rayIntersectsBox(start, dir, collider, checkDist);
        if (t !== null) {
          return true;
        }
      } else if (collider.type === 'cylinder') {
        const cylCenter = this._scratchCylCenter.set(collider.position.x, collider.position.y, collider.position.z);
        const t = rayIntersectsCylinder(
          start,
          dir,
          cylCenter,
          collider.radius,
          collider.height / 2,
          checkDist,
        );
        if (t !== null) {
          return true;
        }
      } else {
        const sphereCenter = this._scratchSphereCenter.set(collider.position.x, collider.position.y, collider.position.z);
        const t = rayIntersectsSphere(start, dir, sphereCenter, collider.radius, checkDist);
        if (t !== null) {
          return true;
        }
      }
    }

    return false;
  }

  setColliders(colliders: ColliderDef[]): void {
    this.colliders = colliders;
  }

  getColliders(): readonly ColliderDef[] {
    return this.colliders;
  }

  updateConfig(patch: Partial<AnalyticalCollisionConfig>): void {
    this.config = { ...this.config, ...patch };
  }

  getConfig(): AnalyticalCollisionConfig {
    return { ...this.config };
  }
}
